import { useEffect, useState } from 'react';

import { adminClient, type AdminSupportAccess } from '@/api/adminClient';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';

const COLUMNS = [{ label: 'Support person' }, { label: 'App' }, { label: 'When' }, { label: 'Requests' }];

function formatAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/** Every read-only support view of this organization's apps: who, which app, when. The organization's admins see the same list in Settings → Team. */
export function OrgSupportAccess({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<AdminSupportAccess[]>();
  const [error, setError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRows(undefined);
    setError(undefined);
    adminClient
      .listSupportAccess(orgId)
      .then((response) => !cancelled && setRows(response.access))
      .catch((reason: unknown) => !cancelled && setError(reason instanceof Error ? reason.message : 'Failed to load support access'));
    return () => {
      cancelled = true;
    };
  }, [orgId, reloadKey]);

  return (
    <section className="mt-8">
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-foreground">Support access</h2>
        <p className="text-xs text-muted-foreground">Who opened this organization&apos;s apps in the read-only support view (Support Access), newest first.</p>
      </div>
      <DataTable>
        <DataTableHead columns={COLUMNS} />
        {error ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => setReloadKey((key) => key + 1)} />
        ) : !rows ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading support access
          </DataTableMessage>
        ) : rows.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>No one from support has opened this organization&apos;s apps.</DataTableMessage>
        ) : (
          <tbody>
            {rows.map((row) => (
              <DataTableRow key={`${row.supportUserId}:${row.sessionId ?? ''}:${row.day}`}>
                <DataTableCell>
                  <p className="truncate font-medium text-foreground">{row.supportName || row.supportEmail || row.supportUserId}</p>
                  {row.supportEmail && row.supportName ? <p className="truncate text-xs text-muted-foreground">{row.supportEmail}</p> : null}
                </DataTableCell>
                <DataTableCell className="text-xs">{row.appTitle || row.sessionId || 'The organization'}</DataTableCell>
                <DataTableCell className="text-xs text-muted-foreground">
                  {formatAt(row.firstSeenAt)}
                  <span className="block">to {formatAt(row.lastSeenAt)}</span>
                </DataTableCell>
                <DataTableCell className="font-mono text-xs">{row.requestCount}</DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        )}
      </DataTable>
    </section>
  );
}
